// ── imports explicites (Phase 4B, générés puis maintenus à la main) ──
import { lanacSupabaseClient } from "./08-supabase-client.jsx";
// ===== Compression + upload d'images (réduit l'egress/stockage, accélère le chargement) =====
// Compresse une image (File/Blob ou data URL) → Blob JPEG léger (max 1400 px, qualité 0.72).
async function compressImageToBlob(src,maxDim=1280,quality=0.66){
  // Dessine un drawable (img/bitmap) déjà dimensionné vers un canvas → blob JPEG. Renvoie null si toBlob échoue.
  const renderToBlob=async(drawable,w,h)=>{
    try{
      const canvas=document.createElement("canvas");canvas.width=w;canvas.height=h;
      const ctx=canvas.getContext("2d");if(!ctx) return null;
      ctx.drawImage(drawable,0,0,w,h);
      return await new Promise(res=>canvas.toBlob(res,"image/jpeg",quality));
    }catch(e){return null;}
  };
  // Repli robuste (grosses images / limites canvas mobile) : décode via createImageBitmap en
  // redimensionnant AU décodage, puis réduit progressivement maxDim si toBlob rend null.
  const fromBlobBitmap=async blob=>{
    if(!blob||typeof createImageBitmap!=="function") return null;
    let probe=null;try{probe=await createImageBitmap(blob);}catch(e){return null;}
    const w0=probe.width,h0=probe.height;
    if(!w0||!h0){probe.close&&probe.close();return null;}
    let curMax=maxDim,blobOut=null;
    while(!blobOut&&curMax>=64){
      const scale=Math.min(1,curMax/Math.max(w0,h0));
      const rw=Math.max(1,Math.round(w0*scale)),rh=Math.max(1,Math.round(h0*scale));
      let bmp=probe;
      if(scale<1){try{bmp=await createImageBitmap(blob,{resizeWidth:rw,resizeHeight:rh,resizeQuality:"high"});}catch(e){bmp=probe;}}
      blobOut=await renderToBlob(bmp,scale<1?rw:w0,scale<1?rh:h0);
      if(bmp!==probe&&bmp.close)bmp.close();
      curMax=Math.round(curMax/2);
    }
    probe.close&&probe.close();
    return blobOut;
  };
  try{
    // Voie nominale (inchangée) : <img> + data URL pour les strings, createImageBitmap pour les Blob/File.
    let drawable,w0,h0;
    if(typeof src==="string"){
      if(!src.startsWith("data:image")) return null;
      // Charge le data URL dans une <img> (img-src autorise data: — comme l'affichage des photos).
      // Plus robuste que atob/createImageBitmap, et hors du périmètre CSP connect-src.
      try{
        const img=new Image();
        img.decoding="async";
        img.src=src;
        await (img.decode?img.decode().catch(()=>new Promise((res,rej)=>{img.onload=res;img.onerror=rej;})):new Promise((res,rej)=>{img.onload=res;img.onerror=rej;}));
        drawable=img;w0=img.naturalWidth||img.width;h0=img.naturalHeight||img.height;
      }catch(e){drawable=null;}
    }else{
      if(!src||!src.type||!src.type.startsWith("image/")) return null;
      if(typeof createImageBitmap!=="function") return null;
      try{const bmp=await createImageBitmap(src);drawable=bmp;w0=bmp.width;h0=bmp.height;}catch(e){drawable=null;}
    }
    if(drawable&&w0&&h0){
      const scale=Math.min(1,maxDim/Math.max(w0,h0));
      const w=Math.max(1,Math.round(w0*scale)),h=Math.max(1,Math.round(h0*scale));
      let blob=await renderToBlob(drawable,w,h);
      if(drawable.close)drawable.close();
      if(blob) return blob;
    }
    // Replis : la voie nominale a échoué (décodage/canvas) → on repasse par un Blob + createImageBitmap.
    const blob=(typeof src==="string")?await fetch(src).then(r=>r.blob()).catch(()=>null):src;
    return await fromBlobBitmap(blob);
  }catch(e){console.error("[LANAC] compress error:",e?.message||e);return null;}
}
// Blob → data URL (aperçu local instantané). Promesse simple, sans dépendance.
function blobToDataURL(blob){
  return new Promise((res,rej)=>{try{const r=new FileReader();r.onload=ev=>res(ev.target.result);r.onerror=()=>rej(new Error("read"));r.readAsDataURL(blob);}catch(e){rej(e);}});
}
// Fichier image → petite data URL (≤400px, ~30 ko) prête pour un avatar. Réduit À LA SOURCE pour
// qu'aucun base64 lourd n'entre jamais dans l'état ni en base. Repli : data URL brute si la
// compression échoue (rare ; la persistance ne garde alors la photo qu'en local, jamais en base).
async function fileToAvatarDataUrl(file){
  if(!file) return null;
  try{
    const blob=await compressImageToBlob(file,400,0.82);
    if(blob){const url=await blobToDataURL(blob).catch(()=>null);if(url) return url;}
  }catch(e){}
  return await new Promise(res=>{try{const r=new FileReader();r.onload=ev=>res(ev.target.result);r.onerror=()=>res(null);r.readAsDataURL(file);}catch(e){res(null);}});
}
// Verdicts de modération image renvoyés par le Worker (clé = URL R2). Mémoire de session bornée,
// JAMAIS persistée. Sert uniquement à router le statut de l'annonce (validation/refus) après upload.
// Tant que la modération est désactivée côté Worker, aucun verdict n'est jamais enregistré ici → no-op.
const __mediaVerdicts=new Map();
const rememberUploadVerdict=(url,verdict)=>{
  if(!url||!verdict||verdict.skipped) return; // skipped = modération OFF → on n'enregistre rien
  __mediaVerdicts.set(url,verdict);
  if(__mediaVerdicts.size>120){const k=__mediaVerdicts.keys().next().value;__mediaVerdicts.delete(k);} // borne mémoire
};
const takeUploadVerdict=url=>{const v=__mediaVerdicts.get(url);if(v)__mediaVerdicts.delete(url);return v||null;};
//  F1 étape 2 (29/09/2026) — CE QUI EST DÉJÀ PARTI SE RETIENT. Une ardoise peut porter `faits` ({photos:{[i]:{url,verdict}},
//   video}) : chaque envoi RÉUSSI y est noté avec son verdict de modération, pour que la file d'envoi, qui rejoue une
//   publication plus tard (peut-être après un redémarrage), ne renvoie JAMAIS une photo déjà en ligne et ne PERDE jamais
//   son verdict (une photo signalée ne doit pas redevenir « propre » parce que la mémoire de session s'est vidée).
const noterFaitPhoto=(envoi,i,url)=>{if(envoi&&envoi.faits&&envoi.faits.photos)envoi.faits.photos[i]={url,verdict:__mediaVerdicts.get(url)||null};};
// ── LE REFUS NE SE TAIT PLUS (C7 · T28, lot 2) ───────────────────────────────────────────────
//  Un refus NOMMÉ par le serveur (plafond d'octets, cadence, taille, type) n'est PAS une panne de
//  transport. Se rabattre alors sur Supabase Storage (egress PAYANT, aucune modération, aucune garde
//  d'écrasement) ou sur du base64 dans nos propres lignes déverserait exactement ce que la porte
//  vient de refuser. ⭐⭐ UNE PORTE POSÉE SUR UNE ISSUE NE TIENT QUE SI LES AUTRES SONT FERMÉES.
//  ⭐⭐ F1 (29/09/2026) — LE REFUS APPARTIENT À SON ENVOI. Il vivait dans UNE case de module, partagée par toute
//   l'application, et seules les deux écritures d'annonce la vidaient : le refus d'une image de MESSAGE ou d'un AVATAR
//   y restait, et la PROCHAINE écriture d'annonce (accepter une réservation, un rappel…) le lisait comme le sien et
//   renonçait sans rien dire. Désormais chaque envoi porte son ARDOISE (`ouvrirEnvoi()`), passée de main en main :
//   un refus ne regarde que l'envoi qui l'a reçu, et le PREMIER refus est celui qu'on dit.
//  (29/09, F1 étape 2) + les refus DÉFINITIFS du worker que rien ne guérira (clé mal formée, fichier vide, méthode) : tus, ils
//   devenaient une « panne » que la file rejouait 24 h — photos et vidéo renvoyées à chaque palier. `no_token` / `unauthorized`
//   restent une panne : une session se rafraîchit.
const REFUS_NOMMES=new Set(["media_quota","rate_limited","too_large","unsupported_type","forbidden_prefix","forbidden_path","already_exists","bad_key","empty","method_not_allowed"]);
const ouvrirEnvoi=()=>({refus:null});
const noterRefus=(envoi,code,phrase,secondes)=>{if(envoi&&!envoi.refus)envoi.refus={code:String(code),phrase:typeof phrase==="string"?phrase:"",secondes:Number(secondes)||0};};
//  F1 étape 3 — un envoi ABANDONNÉ (la déconnexion) entre deux étapes est nommé tel quel : rien ne part plus.
const abandonne=envoi=>{if(envoi&&envoi.signal&&envoi.signal.aborted)noterRefus(envoi,"abandon");return !!(envoi&&envoi.refus);};
//  ⭐ F1 étape 3 (29/09/2026) — UN ENVOI QUI SE TAIT EST UN ENVOI MORT. `fetch` n'a ni progression d'envoi ni délai : une
//   connexion qui CALE (tunnel, ascenseur, réseau d'hôtel) tenait l'envoi — la publication, et derrière elle toute la file
//   d'envoi — sans fin. L'envoi passe par XMLHttpRequest, qui DIT sa progression ; un CHIEN DE GARDE l'abandonne quand plus
//   UN octet ne part depuis R2_SILENCE_MS — jamais une limite de durée TOTALE : une vidéo lente sur un réseau lent progresse,
//   elle n'est pas coupée —, puis quand, tout parti, la réponse (modération comprise) ne vient pas en R2_REPONSE_MS. L'abandon
//   est SÛR : la clé de publication rend le rejeu idempotent. Un signal (la déconnexion) l'abandonne aussi, et la progression
//   est rendue (`surProgres(chargé, taille)`). Sans XMLHttpRequest (un banc Node, un moteur sans DOM) : fetch, comme avant.
const R2_SILENCE_MS=30000, R2_REPONSE_MS=60000, R2_VEILLE_MS=5000;
const envoyerVersR2=(url,blob,jeton,{signal=null,surProgres=null}={})=>{
  if(typeof XMLHttpRequest==="undefined"){
    return fetch(url,{method:"POST",headers:{"Authorization":"Bearer "+jeton,"Content-Type":blob.type||"image/jpeg"},body:blob,signal:signal||undefined})
      .then(async res=>{let corps=null;try{corps=await res.json();}catch(_){}return {ok:res.ok,status:res.status,corps};},
        e=>({ok:false,status:0,panne:signal&&signal.aborted?"abandon":"reseau",message:String(e?.message||e)}));
  }
  return new Promise(resoudre=>{
    const xhr=new XMLHttpRequest();
    let fini=false,dernier=Date.now(),toutParti=false,veille=null;
    const clore=r=>{if(fini)return;fini=true;clearTimeout(veille);if(signal)signal.removeEventListener("abort",abandonner);resoudre(r);};
    const abandonner=()=>{clore({ok:false,status:0,panne:"abandon"});try{xhr.abort();}catch(_){}};
    const veiller=()=>{veille=setTimeout(()=>{
      if(fini) return;
      if(Date.now()-dernier>=(toutParti?R2_REPONSE_MS:R2_SILENCE_MS)){clore({ok:false,status:0,panne:toutParti?"sans_reponse":"silence"});try{xhr.abort();}catch(_){}return;}
      veiller();
    },R2_VEILLE_MS);};
    xhr.open("POST",url);
    xhr.setRequestHeader("Authorization","Bearer "+jeton);
    xhr.setRequestHeader("Content-Type",blob.type||"image/jpeg");
    if(xhr.upload){
      xhr.upload.onprogress=e=>{dernier=Date.now();if(surProgres)surProgres(e.loaded,e.lengthComputable?e.total:blob.size);};
      xhr.upload.onload=()=>{toutParti=true;dernier=Date.now();};
    }
    xhr.onload=()=>{let corps=null;try{corps=JSON.parse(xhr.responseText);}catch(_){}clore({ok:xhr.status>=200&&xhr.status<300,status:xhr.status,corps});};
    xhr.onerror=()=>clore({ok:false,status:0,panne:"reseau"});
    xhr.onabort=()=>clore({ok:false,status:0,panne:"abandon"});
    if(signal){if(signal.aborted){abandonner();return;}signal.addEventListener("abort",abandonner);}
    veiller();
    try{xhr.send(blob);}catch(e){clore({ok:false,status:0,panne:"reseau"});}
  });
};
//  Un signal BORNÉ : il s'abandonne au bout de `ms`, ou avec son parent ; `fin()` le libère (aucune minuterie qui traîne).
const borneSignal=(parent,ms)=>{
  const c=new AbortController();
  const t=setTimeout(()=>c.abort(),ms);
  const suivre=()=>c.abort();
  if(parent){if(parent.aborted)c.abort();else parent.addEventListener("abort",suivre,{once:true});}
  return {signal:c.signal,fin:()=>{clearTimeout(t);if(parent)parent.removeEventListener("abort",suivre);}};
};
// Upload via le Worker Cloudflare R2 (egress gratuit). Renvoie null si R2 non configuré ou indisponible (→ repli Supabase).
async function uploadToR2(bucket,path,blob,envoi){
  const base=(window.LANAC_R2_UPLOAD_URL||"").trim();
  if(!base||!blob) return null; // R2 non configuré → repli Supabase
  try{
    const client=lanacSupabaseClient();
    let token=null;
    if(client){try{token=(await client.auth.getSession()).data?.session?.access_token||null;}catch(e){}}
    if(!token) return null;
    const key=bucket+"/"+path;
    const surProgres=envoi&&envoi.surProgres?(charge,taille)=>envoi.surProgres(charge,taille,false):null;
    const r=await envoyerVersR2(base.replace(/\/$/,"")+"/upload?key="+encodeURIComponent(key),blob,token,{signal:(envoi&&envoi.signal)||null,surProgres});
    if(!r.ok){
      if(r.status){
        //  Le serveur NOMME ce qu'il refuse : on le retient pour que les replis s'abstiennent et que
        //  l'écran puisse dire SA phrase (c'est lui qui connaît le chiffre — leçon de T26).
        const info=r.corps;
        if(info&&info.error&&REFUS_NOMMES.has(String(info.error)))noterRefus(envoi,info.error,info.phrase,info.retry_after);
        console.error("[LANAC] R2 upload failed:",r.status,(info&&info.error)||"");
      }
      //  Abandonné (la déconnexion, le compte qui change) : nommé, et tu — ce n'est pas une panne à dire.
      else if(r.panne==="abandon") noterRefus(envoi,"abandon");
      else console.error("[LANAC] R2 upload exception:",r.panne==="silence"?"stalled (no progress)":r.panne==="sans_reponse"?"no response":(r.message||"network error"));
      return null;
    }
    if(envoi&&envoi.surProgres) envoi.surProgres(blob.size,blob.size,true);
    const data=r.corps;
    if(data&&data.url){rememberUploadVerdict(data.url,data.moderation);return data.url;} // verdict capté (no-op si modération OFF), URL inchangée
    const pub=(window.LANAC_R2_PUBLIC_BASE||"").trim();
    return pub?pub.replace(/\/$/,"")+"/"+key:null;
  }catch(e){console.error("[LANAC] R2 upload exception:",e?.message||e);return null;}
}
// Supprime un objet R2 via le Worker (purge des vidéos d'annonces clôturées). Silencieux/sans effet si R2 non configuré.
async function deleteFromR2(key){
  try{
    const base=(window.LANAC_R2_UPLOAD_URL||"").trim();
    if(!base||!key) return;
    const client=lanacSupabaseClient();
    let token=null;if(client){try{token=(await client.auth.getSession()).data?.session?.access_token||null;}catch(e){}}
    if(!token) return;
    await fetch(base.replace(/\/$/,"")+"/delete?key="+encodeURIComponent(key),{method:"POST",headers:{"Authorization":"Bearer "+token}}).catch(()=>{});
  }catch(e){}
}
const r2KeyFromUrl=u=>{
  const base=(window.LANAC_R2_PUBLIC_BASE||"").replace(/\/$/,"");
  return (typeof u==="string"&&base&&u.startsWith(base+"/"))?u.slice(base.length+1):null;
};
// Liste blanche de largeurs de vignettes (sécurité + maîtrise du coût Cloudflare : un nombre
// borné de variantes). Toute largeur demandée est arrondie au palier supérieur.
const IMG_WIDTH_BUCKETS=[96,200,400,600,800,1280];
const pickImgWidth=w=>{const n=Number(w);if(!Number.isFinite(n)||n<=0)return null;for(const b of IMG_WIDTH_BUCKETS){if(n<=b)return b;}return IMG_WIDTH_BUCKETS[IMG_WIDTH_BUCKETS.length-1];};
// Résout une valeur média en URL affichable, TOLÉRANT aux deux formats (rétro-compatible) :
//   • base64 (data:) ou blob: → renvoyée telle quelle ;
//   • URL externe non-R2 (ex. Unsplash) → telle quelle (jamais transformée) ;
//   • clé R2 ou URL R2 → URL publique, avec vignette Cloudflare si opts.w fourni + drapeau actif.
// opts.w = largeur souhaitée en CSS px (sera arrondie à un palier). Repli sûr : si le resizing
// est désactivé ou la valeur non-R2, on renvoie l'image pleine → jamais d'image cassée.
const mediaUrl=(v,opts)=>{
  if(typeof v!=="string"||!v) return v;
  if(/^(data:|blob:)/.test(v)) return v;
  const base=(window.LANAC_R2_PUBLIC_BASE||"").replace(/\/$/,"");
  let key;
  if(/^https?:/.test(v)){
    key=r2KeyFromUrl(v);      // URL R2 → clé ; URL externe → null
    if(!key) return v;        // URL externe → telle quelle (jamais transformée)
  }else{
    key=v.replace(/^\/+/,""); // clé brute
  }
  if(!base) return v;
  const w=opts&&window.LANAC_R2_IMAGE_RESIZE?pickImgWidth(opts.w):null;
  return w
    ? `${base}/cdn-cgi/image/width=${w},quality=80,format=auto/${key}`
    : `${base}/${key}`;
};
// Extrait la clé R2 d'une valeur média, qu'elle soit déjà une clé ou une URL R2 complète.
// Renvoie null pour base64/blob ou une URL non-R2 (rien à supprimer côté R2).
const r2KeyOf=v=>{
  if(typeof v!=="string"||!v||/^(data:|blob:)/.test(v)) return null;
  return r2KeyFromUrl(v) || (/^https?:/.test(v)?null:v.replace(/^\/+/,""));
};
// Purge R2 des PHOTOS d'une annonce supprimée. La clé R2 contient l'id de l'annonce → unique à elle.
// Garde de sécurité : on ne supprime jamais une clé encore référencée par une autre annonce (others),
// au cas (théorique) où une URL serait partagée. base64/blob/URL externe → r2KeyOf=null → ignorées.
// Best-effort : deleteFromR2 est silencieux/sans effet en cas d'échec → ne casse jamais la suppression.
const purgeListingPhotosR2=(item,others=[])=>{
  if(!item) return;
  const keysOf=o=>[o&&o.img,...(Array.isArray(o&&o.photos)?o.photos:[])].map(r2KeyOf).filter(Boolean);
  const used=new Set();
  (Array.isArray(others)?others:[]).forEach(o=>keysOf(o).forEach(k=>used.add(k)));
  [...new Set(keysOf(item))].filter(k=>!used.has(k)).forEach(k=>deleteFromR2(k));
};
// Upload un Blob vers un bucket public, cache long (revisites = 0 egress) → renvoie l'URL publique.
// R2 d'abord si configuré, sinon Supabase Storage (comportement actuel). Repli sûr → rien ne casse.
//  ⭐ A (29/09/2026) — UNE PHOTO D'ANNONCE NE VIT PAS DANS SUPABASE : pas de repli Storage pour `listing-images` (egress PAYANT
//   à chaque affichage, tant que l'annonce vit). La base le refuse aussi (plus aucune politique d'écriture sur ce seau, et
//   `listings_media_are_addresses` refuse un hôte Supabase). Une panne R2 est une panne d'envoi NOMMÉE (l'appelant le dit).
//   La messagerie et l'avatar gardent leur repli : décision séparée.
const SANS_REPLI_STORAGE=new Set(["listing-images"]);
async function uploadImageBlob(bucket,path,blob,envoi){
  if(!blob) return null;
  const e=envoi||ouvrirEnvoi(); // un appelant sans ardoise (message, avatar) a la sienne, locale à CET envoi
  const r2=await uploadToR2(bucket,path,blob,e);
  if(r2) return r2;
  //  ⭐⭐ LE SERVEUR A DIT NON : on ne déverse pas ailleurs. Supabase Storage, c'est de l'egress
  //   payant, sans modération ni garde d'écrasement — l'issue de secours ne doit pas être la
  //   porte de service de ce qu'on vient de refuser.
  if(e.refus||SANS_REPLI_STORAGE.has(bucket)) return null;
  try{
    const client=lanacSupabaseClient();
    if(!client) return null;
    const {error}=await client.storage.from(bucket).upload(path,blob,{contentType:"image/jpeg",upsert:true,cacheControl:"31536000"});
    if(error){console.error("[LANAC] storage upload error:",error.message);return null;}
    return client.storage.from(bucket).getPublicUrl(path).data?.publicUrl||null;
  }catch(e){console.error("[LANAC] storage upload exception:",e?.message||e);return null;}
}
// img base64 d'annonce → compressé + uploadé (URL permanente).
//  F1 (29/09/2026) — PLUS DE REPLI BASE64 POUR UNE ANNONCE : la base le refuse (`listings_media_are_addresses`, une photo
//   d'annonce est une ADRESSE). Un échec est NOMMÉ sur l'ardoise de l'appelant (`upload_failed`), qui n'écrira pas ; la
//   valeur d'entrée revient telle quelle — l'outil de migration d'administration, sans ardoise, compare lui-même le
//   résultat à une URL et ne l'écrit que si c'en est une.
async function ensureStoredListingImg(img,id,envoi){
  if(typeof img!=="string"||!img.startsWith("data:image")) return img;
  if(envoi&&envoi.refus) return img; // envoi déjà refusé : rien ne part (l'appelant n'écrira pas)
  const client=lanacSupabaseClient();
  let uid=null;if(client){try{uid=(await client.auth.getSession()).data?.session?.user?.id;}catch(e){}}
  const blob=uid?await compressImageToBlob(img):null;
  const url=blob?await uploadImageBlob("listing-images",`${uid}/${id||Date.now()}_${Date.now()}.jpg`,blob,envoi):null;
  if(url) return url;
  noterRefus(envoi,"upload_failed");
  return img;
}
// Avatar (photo de profil) en base64 → compressé (petit, suffisant pour un rond) + uploadé sur R2
// dans le bucket "profile-photos". Renvoie l'URL publique, ou la valeur d'origine si non base64 / échec
// (repli sûr : on ne perd jamais la photo). N'IMPACTE PAS les flux annonces / vidéos / messages.
async function ensureStoredAvatar(avatar,uid){
  if(typeof avatar!=="string"||!avatar.startsWith("data:image")) return avatar; // déjà une URL ou vide → rien à faire
  if(!uid) return avatar;
  const blob=await compressImageToBlob(avatar,400,0.82); // un avatar n'a pas besoin d'être grand
  if(!blob) return avatar;
  const url=await uploadImageBlob("profile-photos",`${uid}/avatar_${Date.now()}.jpg`,blob);
  // On stocke la CLÉ R2 (résiliente au changement de domaine) ; si repli Supabase Storage
  // (URL non-R2), on garde l'URL complète. mediaUrl() gère les deux à l'affichage.
  return url?(r2KeyFromUrl(url)||url):avatar; // repli base64 si l'upload échoue
}
// Agrège les verdicts de modération image (Worker AWS Rekognition) des photos qu'on vient d'uploader.
// Renvoie null si aucun verdict bloquant (tout clean OU modération désactivée) → comportement inchangé.
const aggregateMediaModeration=verdicts=>{
  const arr=Array.isArray(verdicts)?verdicts:[];
  if(!arr.length) return null;
  const list=arr.filter(Boolean);
  const flagged=list.some(v=>v.flagged);
  const unknown=list.some(v=>v.unknown);
  if(flagged||unknown){
    const hard=list.some(v=>v.flagged&&v.hard);
    const labels=list.reduce((a,v)=>a.concat(Array.isArray(v.labels)?v.labels:[]),[]);
    const maxConfidence=list.reduce((m,v)=>Math.max(m,Number(v.maxConfidence||0)),0);
    return {flagged,hard,unknown,labels,maxConfidence};
  }
  // Aucun verdict bloquant. checkedClean = STRICT : CHAQUE photo a un verdict AWS propre (aucun null /
  // skipped / flagged / unknown). Une seule image non vérifiée (AWS off sur l'une, repli base64, etc.)
  // → on renvoie null, donc PAS de signal "vérifié propre" → aucun upgrade trust possible.
  const allChecked=arr.every(v=>v&&!v.skipped&&!v.flagged&&!v.unknown);
  if(allChecked) return {flagged:false,hard:false,unknown:false,checkedClean:true,labels:[],maxConfidence:list.reduce((m,v)=>Math.max(m,Number(v.maxConfidence||0)),0)};
  return null; // images analysées propres mais incomplètes, OU modération OFF → rien (comportement inchangé)
};
// Toutes les photos d'annonce → URLs R2. img = 1ère photo. Trois espèces d'entrée :
//   · https:            → déjà en ligne (édition), passe VERBATIM — la branche que S24 garde ;
//   · data:image        → compressée ICI puis uploadée (chemin historique : admin-migration,
//                         repli du formulaire quand la compression à l'entrée a échoué) ;
//   · Blob dans item.photoFiles[i] (vague 3, 01/09/2026) → uploadé DIRECT, ZÉRO recompression.
//     Le formulaire compresse désormais À L'AJOUT (l'état ne porte plus ~64 Mo de base64) et
//     transporte le Blob dans un canal ALIGNÉ PAR INDEX — le jumeau exact de videoFile. Le
//     `photos[i]` correspondant est une URL blob: D'APERÇU : elle ne doit JAMAIS atteindre la
//     DB (morte à la fermeture de l'onglet).
//  ⭐⭐ F1 (29/09/2026) — PLUS DE REPLI BASE64. Une photo d'annonce est une ADRESSE : la base refuse les octets par
//   NOM (`listings_media_are_addresses`), et le repli base64 était de toute façon MORT depuis le 20/09 (l'index de T28
//   le refusait, `54000`). Une photo qui n'a pas pu partir — refus nommé du serveur, panne d'envoi, pas de session —
//   est dite sur l'ARDOISE de l'appelant (`envoi`, jamais une case partagée) ; l'appelant n'écrit rien, le formulaire
//   garde tout. Une blob: SANS son Blob (orpheline : rien à envoyer, morte) reste écartée, comme avant.
// `_di` : injection de dépendances POUR LE BANC NODE uniquement (uploader + uid) — le banc
// teste CE code, pas une réplique, et ne peut ni ouvrir de session ni toucher R2. Les appels
// de production ne passent JAMAIS ce paramètre : les défauts sont le comportement réel.
async function ensureStoredListingPhotos(item,envoi,_di={}){
  const photos=Array.isArray(item.photos)?item.photos.filter(Boolean):[];
  const files=Array.isArray(item.photoFiles)?item.photoFiles:[];
  if(!photos.length){const {photoFiles:_pf,...rest}=item;return {...rest,img:await ensureStoredListingImg(item.img,item.id,envoi)};}
  const upload=_di.upload||uploadImageBlob;
  let uid=_di.uid??null;
  if(uid==null){
    const client=lanacSupabaseClient();
    if(client){try{uid=(await client.auth.getSession()).data?.session?.user?.id;}catch(e){}}
  }
  const out=[];
  //  Un envoi déjà refusé n'ENVOIE plus rien : l'appelant n'écrira pas, chaque photo de plus serait une requête (et une
  //   modération) pour rien — mesuré par S119 : deux envois vers R2 pour un seul refus. Les adresses déjà en ligne, elles,
  //   passent toujours telles quelles (aucun réseau).
  const refuse=()=>abandonne(envoi);
  for(let i=0;i<photos.length;i++){
    const p=photos[i];
    const blobDirect=files[i]&&typeof files[i].arrayBuffer==="function"?files[i]:null;
    if(blobDirect){
      const url=uid&&!refuse()?await upload("listing-images",`${uid}/${item.id||Date.now()}_${Date.now()}_${i}.jpg`,blobDirect,envoi):null;
      if(url){out.push(url);noterFaitPhoto(envoi,i,url);continue;}
      noterRefus(envoi,"upload_failed"); // un refus NOMMÉ déjà noté passe devant (le premier est celui qu'on dit)
      continue;
    }
    if(typeof p==="string"&&p.startsWith("blob:"))continue; // orpheline : rien à envoyer, morte après l'onglet
    if(typeof p==="string"&&p.startsWith("data:image")){
      const blob=uid&&!refuse()?await compressImageToBlob(p):null;
      const url=blob?await upload("listing-images",`${uid}/${item.id||Date.now()}_${Date.now()}_${i}.jpg`,blob,envoi):null;
      if(url){out.push(url);noterFaitPhoto(envoi,i,url);continue;}
      noterRefus(envoi,"upload_failed");
      continue;
    }
    out.push(p);
  }
  const img=(out[0]&&/^https?:/.test(String(out[0])))?out[0]:await ensureStoredListingImg(item.img,item.id,envoi);
  // Verdicts de modération des photos fraîchement uploadées (no-op si modération Worker OFF).
  const mediaModeration=aggregateMediaModeration(out.map(u=>takeUploadVerdict(u)));
  const {photoFiles:_pf,...rest}=item; // le canal Blob ne sort JAMAIS de la passerelle (ni état global, ni DB)
  // img ne peut JAMAIS rester une URL blob: (le formulaire pose img=photos[0], qui peut
  // en être une) : on la remplace par la 1ʳᵉ photo stockée, ou "" (l'UI a déjà ses replis
  // NO_PHOTO — une chaîne vide est neutre, une blob: morte est une image cassée en prod).
  return {...rest,photos:out,img:(typeof img==="string"&&img.startsWith("blob:"))?(out[0]||""):img,...(mediaModeration?{mediaModeration}:{})};
}
// Vidéo d'annonce : si un nouveau fichier est présent (_file), l'uploader sur R2 et stocker la VRAIE URL.
//  F1 (29/09/2026) — une vidéo AJOUTÉE qui n'a pas pu partir ne donne plus une annonce « publiée sans » en silence :
//   l'échec est NOMMÉ sur l'ardoise de l'appelant, qui n'écrit rien (même règle que les photos) ; le retour reste sans
//   vidéo — jamais une blob: morte. La VIGNETTE, elle, est dérivée : si elle seule échoue, la vidéo part sans elle.
async function ensureStoredListingVideo(item,envoi){
  // Le fichier vidéo transite via item.videoFile (le champ item.video est écrasé par l'URL côté formulaire)
  const file=(item&&item.videoFile)||(item&&item.video&&item.video._file)||null;
  if(!file) return item; // pas de nouvelle vidéo → on ne touche à rien
  if(abandonne(envoi)) return item; // l'envoi est déjà refusé (ou abandonné) : la vidéo (jusqu'à 50 Mo) ne part pas pour rien
  const sans={...item,videoFile:null,video:null,videoUrl:null,videoPath:null};
  const client=lanacSupabaseClient();
  let uid=null;if(client){try{uid=(await client.auth.getSession()).data?.session?.user?.id;}catch(e){}}
  if(!uid){noterRefus(envoi,"upload_failed");return sans;}
  const ext=(((file.name||"").split(".").pop())||"mp4").toLowerCase().replace(/[^a-z0-9]/g,"")||"mp4";
  const stamp=`${item.id||Date.now()}_${Date.now()}`;
  const vkey=`listing-videos/${uid}/${stamp}.${ext}`;
  const vurl=await uploadToR2("listing-videos",`${uid}/${stamp}.${ext}`,file,envoi);
  if(!vurl){noterRefus(envoi,"upload_failed");return sans;}
  let thumbUrl=item.videoThumb||(item.video&&item.video.thumb)||null;
  if(typeof thumbUrl==="string"&&thumbUrl.startsWith("data:")){
    const tblob=await compressImageToBlob(thumbUrl,640,0.6);
    const t=tblob?await uploadToR2("listing-videos",`${uid}/${stamp}_thumb.jpg`,tblob,envoi):null;
    thumbUrl=t||null; // évite un thumbnail base64 lourd en base
  }
  const partie={videoUrl:vurl,video:vurl,videoPath:vkey,videoBucket:"r2",videoThumb:thumbUrl,videoMeta:{...(item.videoMeta||{}),path:vkey,bucket:"r2"}};
  if(envoi&&envoi.faits)envoi.faits.video=partie; // F1 étape 2 : la vidéo partie ne repartira pas
  return {...item,videoFile:null,...partie};
}
//  ⭐ F1 (29/09/2026) — L'ÉCRAN RECOPIE LA RÈGLE DU SERVEUR (`lanac_listing_media_is_address`) : même lecture — celle
//   d'un navigateur (contrôles et blancs de tête retirés, tabulation / saut de ligne / retour chariot retirés partout,
//   schéma sans casse) —, même plafond EN OCTETS (2 048), même table de vérité (le banc les confronte ligne à ligne).
//   Jugée sur la LIGNE qui part (après listingToDB, les colonnes mêmes de la contrainte) : une annonce qui embarquerait
//   ses octets n'est pas ENVOYÉE — des Mo pour un refus certain.
//  ⭐ A (29/09/2026) — … et jamais un hôte Supabase : l'AUTORITÉ est lue comme la lit le navigateur (après un schéma http(s),
//   barres obliques absentes ou inverses comprises ; relatif au protocole ; hôte après le DERNIER arobase ; port retiré ; point
//   final) — la même expression régulière qu'en base, confrontée par le banc.
const MEDIA_ADRESSE_MAX_OCTETS=2048;
const HOTE_SUPABASE=/(^|\.)supabase\.(co|in)\.?$/;
const mediaEstUneAdresse=v=>{
  if(v==null) return true;
  const s=String(v);
  if(new TextEncoder().encode(s).length>MEDIA_ADRESSE_MAX_OCTETS) return false;
  const lu=s.replace(/^[\x01-\x20]+/,"").replace(/[\t\n\r]/g,"");
  const schema=lu.match(/^([A-Za-z][A-Za-z0-9+.-]*):/);
  if(schema&&!/^https?$/i.test(schema[1])) return false;
  const autorite=/^https?:/i.test(lu)?lu.match(/^[A-Za-z]+:[/\\]*(?:[^/\\?#]*@)?([^/\\?#:]*)/)
    :/^[/\\]{2}/.test(lu)?lu.match(/^[/\\]*(?:[^/\\?#]*@)?([^/\\?#:]*)/):null;
  return !(autorite&&HOTE_SUPABASE.test(autorite[1].toLowerCase()));
};
const LIGNE_MEDIAS=["img","video_url","video_thumb","video_path"];
const ligneAnnonceSansOctets=row=>!row||((!Array.isArray(row.photos)||row.photos.every(mediaEstUneAdresse))&&LIGNE_MEDIAS.every(c=>mediaEstUneAdresse(row[c])));
// Applique le verdict de modération image au STATUT de l'annonce (le seul signal de modération persisté
// en DB — donc aucun nouvel octet, aucune colonne, egress/pagination inchangés). hard (≥95% nudité/drogue/
// arme) → rejet net ; flaggé non-hard OU indéterminé (panne AWS, fail-open) → validation manuelle.
// Ne dégrade jamais une annonce déjà clôturée/refusée. No-op total si aucun verdict (modération OFF).
const applyMediaModerationStatus=itm=>{
  const mm=itm&&itm.mediaModeration;
  if(!mm||(!mm.flagged&&!mm.unknown)) return itm;
  if(["sold","rented","completed","rejected"].includes(itm.status)) return itm;
  const status=mm.hard?"rejected":"pending";
  const aiFlags=[
    ...(mm.labels||[]).map(l=>({type:mm.hard?"danger":"warning",msg:`Image signalée : ${l.parent||l.name} (${l.conf}%)`,points:mm.hard?80:40})),
    ...(mm.unknown?[{type:"warning",msg:"Contrôle image indisponible : vérification manuelle requise",points:30}]:[]),
  ];
  return {...itm,status,moderationDecision:status,moderationFlags:[...(itm.moderationFlags||[]),...aiFlags]};
};

export { IMG_WIDTH_BUCKETS, MEDIA_ADRESSE_MAX_OCTETS, R2_REPONSE_MS, R2_SILENCE_MS, __mediaVerdicts, aggregateMediaModeration, borneSignal, envoyerVersR2, ouvrirEnvoi, mediaEstUneAdresse, ligneAnnonceSansOctets, applyMediaModerationStatus, blobToDataURL, compressImageToBlob, deleteFromR2, ensureStoredAvatar, ensureStoredListingImg, ensureStoredListingPhotos, ensureStoredListingVideo, fileToAvatarDataUrl, mediaUrl, pickImgWidth, purgeListingPhotosR2, r2KeyFromUrl, r2KeyOf, rememberUploadVerdict, takeUploadVerdict, uploadImageBlob, uploadToR2 };
